// App.js (or wherever your AppContent lives)
import React, { useEffect, useState, useRef } from 'react';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { SocketProvider } from './socket/socketContext';
import store from './redux/store';
import { motion, AnimatePresence } from 'framer-motion';
import checkAuth from './checkAuth';
import Login from './components/Login';
import Header from './components/header/Header';
import MessageThread from './components/container/thread/MessageThread.jsx';
import ChatInput from './components/container/input/ChatInput.jsx';
import Recents from './components/container/recents/Recents.jsx';
import Generating from './components/container/input/Generating.jsx';
import BuyTarn from './components/dialogs/buy-credits/BuyTarn.jsx';
import Consent from './components/dialogs/Consent.jsx';
import SendFeedback from './components/dialogs/SendFeedback';
import Settings from './components/dialogs/settings/Settings.jsx';
import Notification from './components/dialogs/Notification.jsx';
import TooManyRecords from './components/dialogs/batch-update/TooManyRecords.jsx';
import AddAiFilter from './components/dialogs/AddAiFilter.jsx';
import AddAiField from './components/dialogs/AddAiField.jsx';
import Instructions from './components/dialogs/instructions/Instructions.jsx';
import ReportToSalesforce from './components/dialogs/report-to-salesforce/ReportToSalesforce.jsx';
import { setNegativeCredits, setBuyTarnOpen } from './redux/slices/uiSlice.js';
import { setGenerating, setGeneratingText } from './redux/slices/generatingSlice.js';
import { setScreenshot } from './redux/slices/feedbackSlice';
import html2canvas from 'html2canvas';
import InputContext from './components/container/input/InputContext.jsx';
import TextThread from './components/container/thread/dashboard/TextThread.jsx';
import DynamicUI from './components/container/thread/dashboard/DynamicUI.jsx';
import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/solid';

/* ------------------------------------------------------------------
   Simple Under Construction Screen (full-screen)
------------------------------------------------------------------ */
function UnderConstruction() {
  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-8">
      <div className="max-w-xl w-full text-center">
        <div className="mx-auto mb-6 h-16 w-16 rounded-2xl bg-white shadow-lg flex items-center justify-center">
          <img
            src="/assets/tarn_Icon_Purple.png"
            alt="Tarn"
            className="h-10 w-10"
          />
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">
          Tarn 3.0
        </h1>
        <p className="mt-2 text-lg text-gray-600">
          Under Construction
        </p>
        <p className="mt-4 text-sm text-gray-500">
          We’re preparing the next release for testing. Please check back soon.
        </p>
        <div className="mt-8">
          <a
            href="mailto:support@tarn.co"
            className="inline-flex items-center rounded-xl border border-indigo-200 bg-white px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 transition shadow-sm"
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
}

function AppContent() {
  const dispatch = useDispatch();

  const scrollContainerRef = useRef(null);
  const bottomRef = useRef(null);

  const [sidebarWidth, setSidebarWidth] = useState(350);
  const minWidth = 350;
  const isDragging = useRef(false);

  const [isCollapsed, setIsCollapsed] = useState(false);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const user = useSelector((s) => s.user.user);
  const isDashboard = useSelector((s) => s.ui.viewMode) === 'dashboard';
  const negativeCredits = useSelector((s) => s.ui.negativeCredits);
  const isBuyTarnOpen = useSelector((s) => s.ui.isBuyTarnOpen);
  const isGeneralFeedbackOpen = useSelector((s) => s.ui.isGeneralFeedbackOpen);
  const isAccountSettingsOpen = useSelector((s) => s.ui.isAccountSettingsOpen);
  const isInstructionsOpen = useSelector((s) => s.ui.isInstructionsOpen);
  const addAiFilter = useSelector((s) => s.ui.isAddAiFilterOpen);
  const addAiField = useSelector((s) => s.ui.isAddAiFieldOpen);
  const reportToSalesforce = useSelector((s) => s.ui.isReportToSalesforceOpen);
  const captureFeedback = useSelector((s) => s.feedback.captureFeedback);
  const isGenerating = useSelector((s) => s.generating.isGenerating);
  const startingUp = useSelector((s) => s.ui.startingUp);
  const messages = useSelector((s) => s.chat.chatSession.messages);
  const recents = useSelector((s) => s.chat.chatSession.recents);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const isAsking = useSelector((s) => s.ui.askingSpecificAgent.isAsking);

  // Initial auth check
  useEffect(() => {
    (async () => {
      const { isAuthenticated } = await checkAuth();
      setIsAuthenticated(isAuthenticated);
      setLoading(false);
    })();
  }, []);

  /* NEW: auth heartbeat poll (10 min) */
  useEffect(() => {
    if (!isAuthenticated) return;

    const poll = async () => {
      try {
        const { isAuthenticated: ok } = await checkAuth();
        setIsAuthenticated(ok); /* FIX: use 'ok' from checkAuth */
      } catch {
        // ignore transient network errors; next tick will try again
      }
    };

    const t0 = setTimeout(poll, 10000);           // first check after 10s
    const id = setInterval(poll, 10 * 60 * 1000); // then every 10 minutes
    return () => { clearTimeout(t0); clearInterval(id); };
  }, [isAuthenticated]);

  // Credits flag
  useEffect(() => {
    if (user?.creditBalance?.$numberDecimal) {
      const balance = parseFloat(user.creditBalance.$numberDecimal);
      dispatch(setNegativeCredits(balance < 0));
    } else {
      dispatch(setNegativeCredits(false));
    }
  }, [user, dispatch]);

  // Startup spinner text
  useEffect(() => {
    if (startingUp) {
      dispatch(setGenerating(true));
      dispatch(setGeneratingText('Setting up your secure Tarn chat'));
    } else {
      dispatch(setGenerating(false));
    }
  }, [startingUp, dispatch]);

  // Screenshot capture
  useEffect(() => {
    if (!captureFeedback) return;
    const root = document.getElementById('app-screenshot-target');
    if (!root) return;
    html2canvas(root)
      .then((c) => dispatch(setScreenshot(c.toDataURL('image/png'))))
      .catch((err) => console.error('Screenshot error:', err));
  }, [captureFeedback, dispatch]);

  // Scroll helpers
  useEffect(() => {
    const handleVis = () => {
      const c = scrollContainerRef.current;
      if (!c) return;
      setIsAtBottom(c.getBoundingClientRect().bottom <= window.innerHeight + 5);
    };
    window.addEventListener('scroll', handleVis);
    window.addEventListener('resize', handleVis);
    handleVis();
    return () => {
      window.removeEventListener('scroll', handleVis);
      window.removeEventListener('resize', handleVis);
    };
  }, []);

  useEffect(() => {
    if (!scrollContainerRef.current || !bottomRef.current) return;
    const t = setTimeout(() => {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }, 200);
    return () => clearTimeout(t);
  }, [messages.length]);

  // Early returns AFTER all hooks are declared
  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }
  if (!isAuthenticated) {
    return <Login onLoginSuccess={() => setIsAuthenticated(true)} />;
  }

  const showConsent =
    user &&
    user.consentRecord &&
    (!user.consentRecord.dataPrivacyPolicy ||
      !user.consentRecord.tarnTerms ||
      !user.consentRecord.openAiTerms);
  const shouldShowRecents =
    !isDashboard && !isGenerating && !isAsking && recents?.length > 0 && isAtBottom;
  const recentsVariant = { hidden: { opacity: 0, y: 50 }, visible: { opacity: 1, y: 0 } };

  const startDrag = (e) => {
    e.preventDefault();
    isDragging.current = true;
    document.addEventListener('mousemove', onDrag);
    document.addEventListener('mouseup', stopDrag);
  };
  const onDrag = (e) => {
    if (!isDragging.current) return;
    setSidebarWidth(Math.max(minWidth, window.innerWidth - e.clientX));
  };
  const stopDrag = () => {
    isDragging.current = false;
    document.removeEventListener('mousemove', onDrag);
    document.removeEventListener('mouseup', stopDrag);
  };

  const socketUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5002';
  const actualWidth = isCollapsed ? '50px' : `${sidebarWidth}px`;

  return (
    <SocketProvider url={socketUrl}>
      <div
        id="app-screenshot-target"
        className={`relative flex flex-col items-center w-full ${
          isDashboard ? 'h-screen overflow-hidden' : 'min-h-screen overflow-x-hidden'
        }`}
      >
        {isDashboard && <Header />}
        {isBuyTarnOpen && <BuyTarn />}
        {isGeneralFeedbackOpen && <SendFeedback />}
        {isAccountSettingsOpen && <Settings />}
        {isInstructionsOpen && <Instructions />}
        {addAiFilter.isOpen && <AddAiFilter />}
        {addAiField.isOpen && <AddAiField />}
        {reportToSalesforce.isOpen && <ReportToSalesforce />}
        <Notification />
        {showConsent && <Consent />}
        <TooManyRecords />

        {!isDashboard ? (
          <div className="flex-1 w-full mt-[46px] flex flex-col relative">
            <div
              ref={scrollContainerRef}
              className="flex-1 overflow-y-auto pb-[12rem]"
            >
              <MessageThread />
              <div ref={bottomRef} />
            </div>
          </div>
        ) : (
          <div className="flex-1 w-full flex flex-row relative overflow-hidden">
            <div className="flex-1 overflow-y-auto">
              <DynamicUI />
            </div>

            <div
              style={{
                width: actualWidth,
                transition: isDragging.current ? 'none' : 'width 0.2s ease',
              }}
              className="relative flex flex-col"
            >
              {!isCollapsed && (
                <div
                  onMouseDown={startDrag}
                  className="absolute -left-1 top-0 z-50"
                  style={{
                    height: 'calc(100% - 4rem)',
                    width: '12px',
                    cursor: 'col-resize',
                  }}
                >
                  <div className="absolute inset-y-3 left-1/2 -translate-x-1/2 w-px bg-gray-200 pointer-events-none" />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsCollapsed(true);
                    }}
                    className="absolute -bottom-8 left-1/2 -translate-x-1/2 pl-1"
                  >
                    <ArrowRightOnRectangleIcon className="h-6 w-6 text-indigo-500" />
                  </button>
                </div>
              )}

              {isCollapsed ? (
                <motion.div
                  onClick={() => setIsCollapsed(false)}
                  className="h-full border border-indigo-200 rounded-tl-xl rounded-bl-xl
                             cursor-pointer flex flex-col justify-end items-center my-12"
                  animate={{
                    boxShadow: [
                      '0 0 3px 2px rgba(165,180,252,0.4)',
                      '0 0 8px 4px rgba(165,180,252,0.2)',
                      '0 0 4px 3px rgba(165,180,252,0.4)',
                    ],
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <img
                    src="/assets/tarn_Icon_Purple.png"
                    alt="Tarn"
                    className="h-8 w-8 mb-3"
                  />
                </motion.div>
              ) : (
                <div
                  ref={scrollContainerRef}
                  className="flex-1 overflow-y-auto"
                >
                  <TextThread />
                  <div ref={bottomRef} />
                </div>
              )}
            </div>
          </div>
        )}

        {!isCollapsed && (
          <div
            className={`fixed bottom-0 left-0 w-full flex ${
              isDashboard ? 'justify-end' : 'justify-center'
            } z-40`}
          >
            {!isDashboard && (shouldShowRecents || isGenerating) && (
              <div className="fixed bottom-0 w-full h-[6.75rem] bg-[#F8F8FF] z-10" />
            )}
            <div
              className="relative px-2"
              style={
                isDashboard
                  ? { width: sidebarWidth }
                  : { width: '100%', maxWidth: 800 }
              }
            >
              <AnimatePresence>
                {shouldShowRecents && (
                  <motion.div
                    key="recents"
                    variants={recentsVariant}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    transition={{ duration: 0.3 }}
                    className="absolute bottom:[6.75rem] bg-[#F8F8FF] left-0 w-full z-20"
                  >
                    <Recents />
                  </motion.div>
                )}
              </AnimatePresence>
              <AnimatePresence>
                {isAsking && (
                  <motion.div
                    key="inputContext"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 50 }}
                    transition={{ duration: 0.3 }}
                    className="absolute bottom-[6.75rem] bg-[#F8F8FF] left-0 w-full z-20"
                  >
                    <InputContext />
                  </motion.div>
                )}
              </AnimatePresence>
              <AnimatePresence>
                {isGenerating && (
                  <motion.div
                    key="generating"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 50 }}
                    transition={{ duration: 0.3 }}
                    className="absolute bottom-[6.75rem] bg-[#F8F8FF] left-1 w-full z-20"
                  >
                    <Generating />
                  </motion.div>
                )}
              </AnimatePresence>
              <div className="relative z-20">
                <ChatInput />
              </div>
            </div>
          </div>
        )}
      </div>
    </SocketProvider>
  );
}

function App() {
  // Show maintenance screen when building for production.
  // Optional override: set REACT_APP_MAINTENANCE=true to force this screen in any env.
  const isProd = process.env.NODE_ENV === 'production';
  const forceMaintenance = String(process.env.REACT_APP_MAINTENANCE || '').toLowerCase() === 'true';

  if (isProd || forceMaintenance) {
    return <UnderConstruction />;
  }

  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;